import React, { useState } from 'react';
import { INDIAN_STATES } from '../constants/indianStates';

const emptyForm = {
  label: 'Home',
  name: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  pincode: '',
  isDefault: true,
};

export function formatAddress(a) {
  if (!a) return '';
  return [a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(', ');
}

export default function AddressForm({
  initial,
  defaults = {},
  submitLabel = 'Save address',
  busy = false,
  onSubmit,
  onCancel,
  showDefaultToggle = true,
  compact = false,
}) {
  const [form, setForm] = useState(() => ({ ...emptyForm, ...defaults, ...initial }));

  const set = (key) => (e) => {
    let v = e.target.value;
    if (key === 'phone') v = v.replace(/\D/g, '').slice(0, 10);
    if (key === 'pincode') v = v.replace(/\D/g, '').slice(0, 6);
    setForm((f) => ({ ...f, [key]: v }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.({
      ...form,
      name: form.name.trim(),
      line1: form.line1.trim(),
      line2: form.line2.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      label: form.label.trim() || 'Home',
    });
  };

  const fieldClass =
    'mt-1.5 w-full bg-white border border-[#E5DCC9] px-3 py-2.5 text-sm outline-none focus:border-[#C9A961]';

  const stateValue = form.state || '';

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-3' : 'space-y-4'}>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Label</span>
          <select value={form.label} onChange={set('label')} className={fieldClass}>
            <option>Home</option>
            <option>Work</option>
            <option>Other</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Mobile</span>
          <input
            required
            type="tel"
            value={form.phone}
            onChange={set('phone')}
            className={fieldClass}
            placeholder="10-digit mobile"
            autoComplete="tel"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Full name</span>
        <input required value={form.name} onChange={set('name')} className={fieldClass} autoComplete="name" />
      </label>

      <label className="block">
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Address line 1</span>
        <input
          required
          value={form.line1}
          onChange={set('line1')}
          className={fieldClass}
          placeholder="House / street"
          autoComplete="address-line1"
        />
      </label>

      <label className="block">
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Address line 2</span>
        <input
          value={form.line2}
          onChange={set('line2')}
          className={fieldClass}
          placeholder="Landmark (optional)"
          autoComplete="address-line2"
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">City</span>
          <input required value={form.city} onChange={set('city')} className={fieldClass} autoComplete="address-level2" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">State</span>
          <select
            required
            value={stateValue}
            onChange={set('state')}
            className={fieldClass}
            autoComplete="address-level1"
          >
            <option value="" disabled>
              Select state
            </option>
            {stateValue && !INDIAN_STATES.includes(stateValue) && (
              <option value={stateValue}>{stateValue}</option>
            )}
            {INDIAN_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#1A2332]/60">Pincode</span>
        <input
          required
          value={form.pincode}
          onChange={set('pincode')}
          className={fieldClass}
          inputMode="numeric"
          autoComplete="postal-code"
        />
      </label>

      {showDefaultToggle && (
        <label className="flex items-center gap-2 text-sm text-[#1A2332]/80 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(form.isDefault)}
            onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
            className="accent-[#1A2332]"
          />
          Set as default delivery address
        </label>
      )}

      <div className="flex flex-wrap gap-3 pt-1">
        <button
          type="submit"
          disabled={busy}
          className="bg-[#1A2332] text-[#F5EFE6] px-6 py-3 text-[11px] tracking-[0.25em] uppercase disabled:opacity-60"
        >
          {busy ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-3 text-[11px] tracking-[0.2em] uppercase underline underline-offset-4"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
